import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { createIdempotencyKey } from "@tm/api-utils";
import {
  registerClaim,
  type ClaimRegisterDocument,
  type ClaimRegisterResponse,
} from "@tm/insurance/api";
import { theme } from "@tm/utility/global";

import FormattedText, {
  getPlainText,
  type FormattedTextProps,
  type TextNodeType,
} from "../../../../components/FormattedText/FormattedText";
import WebToast from "../../../../components/Toast/WebToast";
import { ChevronUpIcon } from "../../../../icons";
import type {
  AddDetailsClaimFormCardData,
  ClaimFormField,
  ClaimFormHeaderTitle,
} from "../../types";
import {
  FieldGrid,
  FooterActions,
  FooterNote,
  FormBody,
  FormCard,
  FormHeader,
  PreviousButton,
  PreviousIcon,
  Separator,
  StickyFooter,
  SubmitButton,
} from "./ClaimForm.style";
import { ClaimFormField as ClaimFormFieldRow } from "./ClaimFormField";
import { buildRegisterRequest } from "./registerRequest";
import { uploadClaimFile } from "./submit";
import { validateField, validateForm } from "./validation";

interface FieldCell {
  field: ClaimFormField;
  index: number;
}

type SectionKind = "single" | "pair" | "file" | "textarea";
interface FormSection {
  kind: SectionKind;
  cells: FieldCell[];
}

const DIVIDER_BEFORE: Record<SectionKind, boolean> = {
  single: false,
  pair: true,
  file: true,
  textarea: true,
};

const TOAST_DISMISS_MS = 4000;

type FormToast = { type: "success" | "error"; message: string };

function buildSections(fields: ClaimFormField[]): FormSection[] {
  const sections: FormSection[] = [];
  fields.forEach((field, index) => {
    const cell = { field, index };
    if (field.type === "fileUploader") {
      sections.push({ kind: "file", cells: [cell] });
    } else if (field.key === "DESCRIPTION") {
      sections.push({ kind: "textarea", cells: [cell] });
    } else if (field.type === "datePicker") {
      const last = sections[sections.length - 1];
      if (last?.kind === "single") {
        last.kind = "pair";
        last.cells.push(cell);
      } else {
        sections.push({ kind: "single", cells: [cell] });
      }
    } else {
      sections.push({ kind: "single", cells: [cell] });
    }
  });
  return sections;
}

export interface ClaimFormProps {
  formCard: AddDetailsClaimFormCardData;
  bookingId: string;
  partnerBookingId?: string;
  currency: string;
  benefitKey: string;
  onBack: () => void;
  onSubmitResult: (response: ClaimRegisterResponse) => void;
}

const keyOf = (field: ClaimFormField, index: number): string =>
  field.key ?? String(index);

function normalizeHeaderTitle(
  header?: ClaimFormHeaderTitle
): FormattedTextProps["value"] {
  if (!header) return undefined;
  if (typeof header === "string") return header;
  if ("text" in header && header.text) return header as TextNodeType;
  if ("value" in header && typeof header.value === "string") {
    return {
      text: {
        value: header.value,
        styles: header.styles ?? {},
      },
    };
  }
  return undefined;
}

export function ClaimForm({
  formCard,
  bookingId,
  partnerBookingId,
  currency,
  benefitKey,
  onBack,
  onSubmitResult,
}: ClaimFormProps) {
  const fields: ClaimFormField[] = formCard.claimDetailsSection?.fields ?? [];

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach((field, index) => {
      if (field.value != null && field.value !== "") {
        initial[keyOf(field, index)] = field.value;
      }
    });
    return initial;
  });
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [docsByField, setDocsByField] = useState<
    Record<string, ClaimRegisterDocument[]>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<FormToast | null>(null);
  // Sync lock: `submitting` state alone can miss Enter/double-click before re-render.
  const submitLockRef = useRef(false);
  // One key per submit intent; reuse on retry, clear when the payload changes.
  const idempotencyKeyRef = useRef<string | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const bumpSubmitIntent = () => {
    idempotencyKeyRef.current = null;
  };

  const setFieldError = (key: string, error: string | undefined) =>
    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });

  const handleValueChange = (
    field: ClaimFormField,
    key: string,
    value: string
  ) => {
    bumpSubmitIntent();
    setValues((prev) => ({ ...prev, [key]: value }));
    if (submitted)
      setFieldError(key, validateField(field, value, files[key] ?? []));
  };

  const handleFilesRemove = (
    field: ClaimFormField,
    key: string,
    next: File[]
  ) => {
    bumpSubmitIntent();
    const currentFiles = filesRef.current[key] ?? [];
    setFiles((prev) => ({ ...prev, [key]: next }));
    setDocsByField((prev) => {
      const currentDocs = prev[key] ?? [];
      const kept = currentFiles
        .map((file, idx) => ({ file, doc: currentDocs[idx] }))
        .filter(({ file }) => next.includes(file))
        .map(({ doc }) => doc)
        .filter(Boolean) as ClaimRegisterDocument[];
      return { ...prev, [key]: kept };
    });
    if (submitted)
      setFieldError(key, validateField(field, values[key] ?? "", next));
  };

  const handleFilesPicked = async (
    field: ClaimFormField,
    key: string,
    picked: File[]
  ) => {
    if (!picked.length || uploadingKey) return;

    const validationError = validateField(field, values[key] ?? "", [
      ...(files[key] ?? []),
      ...picked,
    ]);
    if (validationError) {
      setFieldError(key, validationError);
      setToast({ type: "error", message: validationError });
      return;
    }

    setUploadingKey(key);
    setFieldError(key, undefined);

    let lastError = "";

    for (const file of picked) {
      try {
        const doc = await uploadClaimFile(
          file,
          key,
          bookingId,
          partnerBookingId
        );
        bumpSubmitIntent();
        setFiles((prev) => ({
          ...prev,
          [key]: [...(prev[key] ?? []), file],
        }));
        setDocsByField((prev) => ({
          ...prev,
          [key]: [...(prev[key] ?? []), doc],
        }));
      } catch (error) {
        lastError = (error as Error)?.message || "Upload failed.";
        break;
      }
    }

    if (lastError) {
      setFieldError(key, lastError);
      setToast({ type: "error", message: lastError });
    } else {
      setFieldError(key, undefined);
    }

    setUploadingKey(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = validateForm(fields, values, files, keyOf);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (submitting || uploadingKey || submitLockRef.current) return;

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const request = buildRegisterRequest({
        fields,
        values,
        docsByField,
        ...(partnerBookingId ? { partnerBookingId } : {}),
        bookingId,
        currency,
        benefitKey,
        keyOf,
      });
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = createIdempotencyKey();
      }
      const response = await registerClaim(
        request,
        undefined,
        idempotencyKeyRef.current
      );
      idempotencyKeyRef.current = null;
      onSubmitResult(response);
    } catch (error) {
      setToast({
        type: "error",
        message:
          (error as Error)?.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const footerNote = formCard.footerNote;
  const previousCta = formCard.previousCta?.text;
  const submitCta = formCard.submitCta?.text;
  const headerTitle = normalizeHeaderTitle(formCard.headerTitle);
  const hasSubmitLabel = Boolean(getPlainText(submitCta).trim());
  const sections = useMemo(() => buildSections(fields), [fields]);
  const busy = submitting || Boolean(uploadingKey);

  const renderField = ({ field, index }: FieldCell) => {
    const key = keyOf(field, index);
    const isFile = field.type === "fileUploader";
    const locked = Boolean(
      field.readOnly || (field.autoFillIfPreSelected && field.value)
    );
    return (
      <ClaimFormFieldRow
        key={key}
        field={field}
        value={values[key] ?? ""}
        files={files[key] ?? []}
        error={errors[key]}
        uploading={uploadingKey === key}
        disabled={busy || locked}
        onValueChange={(value) => handleValueChange(field, key, value)}
        onFilesChange={(next) => handleFilesRemove(field, key, next)}
        onFilesSelected={
          isFile
            ? (picked) => {
                void handleFilesPicked(field, key, picked);
              }
            : undefined
        }
      />
    );
  };

  return (
    <FormCard>
      <form onSubmit={handleSubmit} noValidate>
        {headerTitle ? (
          <FormHeader>
            <FormattedText value={headerTitle} />
          </FormHeader>
        ) : null}

        <FormBody>
          {sections.map((section, si) => {
            const divider = si > 0 && DIVIDER_BEFORE[section.kind];
            const body =
              section.kind === "single" || section.kind === "pair" ? (
                <FieldGrid>{section.cells.map(renderField)}</FieldGrid>
              ) : (
                renderField(section.cells[0])
              );
            return (
              <Fragment key={si}>
                {divider ? <Separator /> : null}
                {body}
              </Fragment>
            );
          })}
        </FormBody>

        <StickyFooter>
          {getPlainText(footerNote?.message).trim() ? (
            <FooterNote>
              {footerNote?.icon ? (
                <img src={footerNote.icon} alt="" width={16} />
              ) : null}
              <FormattedText value={footerNote?.message} />
            </FooterNote>
          ) : null}

          <FooterActions>
            <PreviousButton type="button" onClick={onBack} disabled={busy}>
              <PreviousIcon aria-hidden="true">
                <ChevronUpIcon color={theme.blueText} cursor="pointer" />
              </PreviousIcon>
              <FormattedText value={previousCta} />
            </PreviousButton>
            <SubmitButton type="submit" disabled={busy || !hasSubmitLabel}>
              <FormattedText value={submitCta} />
            </SubmitButton>
          </FooterActions>
        </StickyFooter>
      </form>

      {toast ? (
        <WebToast
          type={toast.type}
          toastTxt={toast.message}
          position="fixed"
          bottom="24px"
          right="24px"
          width="360px"
          color={toast.type === "success" ? theme.darkGreen : theme.greyText}
        />
      ) : null}
    </FormCard>
  );
}
